import React, { useEffect } from 'react';
import { Mail, Phone, TrendingUp, CheckCircle, BarChart2 } from 'lucide-react';
import { useLeadStats } from '../../hooks/useLeadStats';
import type { Lead } from '../../types/leads';

interface LeadStatsProps {
  leads: Lead[];
}

export function LeadStats({ leads }: LeadStatsProps) {
  const { stats, updateStats } = useLeadStats();

  // Mettre à jour les stats quand les leads changent
  useEffect(() => {
    updateStats(leads);
  }, [leads, updateStats]);

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
        <BarChart2 className="w-5 h-5 text-violet-400" />
        Overview
      </h2>

      <div className="bg-gradient-to-br from-gray-800/70 to-gray-900/80 backdrop-blur-xl rounded-xl p-5 border border-white/10 transform transition-all hover:border-violet-500/20 shadow-lg shadow-black/20 group animate-slide-in-right-1">
        <div className="flex items-center justify-between mb-4">
          <div className="bg-gradient-to-r from-violet-600/30 to-cyan-500/30 p-2.5 rounded-lg group-hover:from-violet-600/40 group-hover:to-cyan-500/40 transition-all duration-300 shadow-md">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <span className="text-sm text-gray-400 bg-white/5 px-2.5 py-1 rounded-lg">Total</span>
        </div>
        <p className="text-3xl font-bold text-white mb-1 group-hover:scale-105 transform transition-transform duration-300 origin-left">{stats.totalLeads}</p>
        <p className="text-gray-400 text-sm">Total collected leads</p>
      </div>

      <div className="bg-gradient-to-br from-gray-800/70 to-gray-900/80 backdrop-blur-xl rounded-xl p-5 border border-white/10 transform transition-all hover:border-green-500/20 shadow-lg shadow-black/20 group animate-slide-in-right-2">
        <div className="flex items-center justify-between mb-4">
          <div className="bg-gradient-to-r from-green-500/30 to-emerald-500/30 p-2.5 rounded-lg group-hover:from-green-500/40 group-hover:to-emerald-500/40 transition-all duration-300 shadow-md">
            <CheckCircle className="w-5 h-5 text-white" />
          </div>
          <span className="text-sm text-gray-400 bg-white/5 px-2.5 py-1 rounded-lg">Processed</span>
        </div>
        <div className="flex items-baseline gap-2 mb-1">
          <p className="text-3xl font-bold text-white group-hover:scale-105 transform transition-transform duration-300 origin-left">{stats.processedCount}</p>
          <p className="text-sm text-green-400 font-medium px-2 py-0.5 bg-green-400/10 rounded-lg">{stats.processedPercentage}%</p>
        </div>
        <p className="text-gray-400 text-sm">of all leads</p>
      </div>

      <div className="bg-gradient-to-br from-gray-800/70 to-gray-900/80 backdrop-blur-xl rounded-xl p-5 border border-white/10 transform transition-all hover:border-violet-500/20 shadow-lg shadow-black/20 group animate-slide-in-right-3">
        <div className="flex items-center justify-between mb-4">
          <div className="bg-gradient-to-r from-violet-600/30 to-cyan-500/30 p-2.5 rounded-lg group-hover:from-violet-600/40 group-hover:to-cyan-500/40 transition-all duration-300 shadow-md">
            <Mail className="w-5 h-5 text-white" />
          </div>
          <span className="text-sm text-gray-400 bg-white/5 px-2.5 py-1 rounded-lg">Emails</span>
        </div>
        <div className="flex items-baseline gap-2 mb-1">
          <p className="text-3xl font-bold text-white group-hover:scale-105 transform transition-transform duration-300 origin-left">{stats.emailCount}</p>
          {stats.totalLeads > 0 && (
            <p className="text-sm text-violet-400 font-medium px-2 py-0.5 bg-violet-400/10 rounded-lg">
              {Math.round((stats.emailCount / stats.totalLeads) * 100)}%
            </p>
          )}
        </div>
        <p className="text-gray-400 text-sm">Email addresses collected</p>
      </div>

      <div className="bg-gradient-to-br from-gray-800/70 to-gray-900/80 backdrop-blur-xl rounded-xl p-5 border border-white/10 transform transition-all hover:border-cyan-500/20 shadow-lg shadow-black/20 group animate-slide-in-right-4">
        <div className="flex items-center justify-between mb-4">
          <div className="bg-gradient-to-r from-violet-600/30 to-cyan-500/30 p-2.5 rounded-lg group-hover:from-violet-600/40 group-hover:to-cyan-500/40 transition-all duration-300 shadow-md">
            <Phone className="w-5 h-5 text-white" />
          </div>
          <span className="text-sm text-gray-400 bg-white/5 px-2.5 py-1 rounded-lg">Phone numbers</span>
        </div>
        <div className="flex items-baseline gap-2 mb-1">
          <p className="text-3xl font-bold text-white group-hover:scale-105 transform transition-transform duration-300 origin-left">{stats.phoneCount}</p>
          {stats.totalLeads > 0 && (
            <p className="text-sm text-cyan-400 font-medium px-2 py-0.5 bg-cyan-400/10 rounded-lg">
              {Math.round((stats.phoneCount / stats.totalLeads) * 100)}%
            </p>
          )}
        </div>
        <p className="text-gray-400 text-sm">Phone numbers collected</p>
      </div>
    </div>
  );
}